import {Injectable} from '@angular/core';
import * as UsersActions from './users.actions';
import {map} from 'rxjs/operators';
import {UsersPartialState} from './users.reducer';
import {Actions, createEffect} from '@ngrx/effects';
import {DataPersistence, withCorrelationIdOf} from '@kion/ngrx-helper';
import {User} from '@kion/data-models';
import {UserConnectors} from "./user.connectors";

@Injectable()
export class UsersEffects {
  inviteUser$ = createEffect(() =>
    this.dataPersistence.fetch(
      UsersActions.inviteUser,
      {
        run: (
          action: ReturnType<typeof UsersActions.inviteUser>
        ) => {
          if (action.isAdmin) {
            return this.backend.inviteAdmin(action.invitation).pipe(
              map(
                () => withCorrelationIdOf(
                  action,
                  UsersActions.inviteUserSuccess({})
                )
              )
            )
          }
          return this.backend.inviteUser(action.invitation).pipe(
            map(
              () => withCorrelationIdOf(
                action,
                UsersActions.inviteUserSuccess({})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof UsersActions.inviteUser>,
          error
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            UsersActions.inviteUserError({error: error})
          );
        }
      }
    )
  );
  validateInvitationToken$ = createEffect(() =>
    this.dataPersistence.fetch(
      UsersActions.validateInvitationToken,
      {
        run: (
          action: ReturnType<typeof UsersActions.validateInvitationToken>
        ) => {
          return this.backend.validateInvitationToken(action.email, action.token).pipe(
            map(
              () => withCorrelationIdOf(
                action,
                UsersActions.validateInvitationTokenSuccess({})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof UsersActions.validateInvitationToken>,
          error
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            UsersActions.validateInvitationTokenError({error: error})
          );
        }
      }
    )
  );
  registerUser$ = createEffect(() =>
    this.dataPersistence.fetch(
      UsersActions.registerUser,
      {
        run: (
          action: ReturnType<typeof UsersActions.registerUser>
        ) => {
          return this.backend.registerUser(
            action.accessToken,
            action.identityToken,
            action.firstName,
            action.lastName,
            action.email,
            action.idp,
            action.oid,
            action.address,
            action.invitationToken
          ).pipe(
            map(
              () => withCorrelationIdOf(
                action,
                UsersActions.registerUserSuccess({})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof UsersActions.registerUser>,
          error: any
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            UsersActions.registerUserError({error: error})
          );
        }
      }
    )
  );
  resendInviteUser$ = createEffect(() =>
    this.dataPersistence.fetch(
      UsersActions.resendInviteUser,
      {
        run: (
          action: ReturnType<typeof UsersActions.resendInviteUser>
        ) => {
          return this.backend.resendInvite(action.invitationId).pipe(
            map(
              () => withCorrelationIdOf(
                action,
                UsersActions.resendInviteUserSuccess({})
              )
            )
          )
        },
        onError: (
          action: ReturnType<typeof UsersActions.resendInviteUser>,
          error
        ) => {
          console.error("Error", error);
          return withCorrelationIdOf(
            action,
            UsersActions.resendInviteUserError({error: error})
          );
        }
      }
    )
  );
  deleteInvitation$ = createEffect(() =>
      this.dataPersistence.fetch(
         UsersActions.deleteInvitation,
         {
            run: (
               action: ReturnType<typeof UsersActions.deleteInvitation>
            ) => {
               return this.backend.deleteInvite(action.invitationId).pipe(
                  map(
                     () => withCorrelationIdOf(
                        action,
                        UsersActions.deleteInvitationSuccess({})
                     )
                  )
               )
            },
            onError: (
               action: ReturnType<typeof UsersActions.deleteInvitation>,
               error
            ) => {
               console.error("Error", error);
               return withCorrelationIdOf(
                  action,
                  UsersActions.deleteInvitationError({error: error})
               );
            }
         }
      )
   );
  loadUserById$ = createEffect(() =>
    this.dataPersistence.fetch(
      UsersActions.loadUserById,
      {
        run: (
          action: ReturnType<typeof UsersActions.loadUserById>
        ) => {
          return this.backend.loadUserById().pipe(
            map(
              (response: User) => withCorrelationIdOf(
                action,
                UsersActions.loadUserByIdSuccess({user: response})
              )
            )
          );
        },
        onError: (
          action: ReturnType<typeof UsersActions.loadUserById>,
          error
        ) => {
          return withCorrelationIdOf(
            action,
            UsersActions.loadUserByIdError({error: error})
          );
        }
      }
    )
  )
  updateB2BUnit$ = createEffect(() =>
    this.dataPersistence.fetch(
      UsersActions.updateB2bUnit,
      {
        run: (action: ReturnType<typeof UsersActions.updateB2bUnit>) => {
          return this.backend.updateB2bUnit(action.b2bUnitId, action.registration).pipe(
            map(
              () => withCorrelationIdOf(action, UsersActions.updateB2bUnitSuccess({}))
            )
          )
        },
        onError: (action: ReturnType<typeof UsersActions.updateB2bUnit>, error) => {
          return withCorrelationIdOf(action, UsersActions.updateB2bUnitError({error}))
        }
      }
    )
  );
  submitContactForm$ = createEffect(() =>
    this.dataPersistence.fetch(
      UsersActions.submitContactForm,
      {
        run: (
          action: ReturnType<typeof UsersActions.submitContactForm>
        ) => {
          return this.backend.sendContactForm(action.data).pipe(
            map(
              (response: any) => withCorrelationIdOf(action, UsersActions.submitContactFormSuccess({data: action.data}))
            )
          )
        },
        onError: (
          action: ReturnType<typeof UsersActions.submitContactForm>,
          error: any
        ) => withCorrelationIdOf(action, UsersActions.submitContactFormError({data: action.data, error: error}))
      }
    )
  );

  constructor(
    private actions$: Actions,
    private dataPersistence: DataPersistence<UsersPartialState>,
    private backend: UserConnectors
  ) {
  }
}

